import { GET_USER, invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import { logger } from '@/utils/logger/logger';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { type components } from '@heidi/schema/ml-scribe';
import { type UserResponse } from '@heidi/workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { produce } from 'immer';

export type SalesBookingFormName = components['schemas']['SalesBookingFormName'];
export type SalesBookingSubmissionRequest =
  components['schemas']['SalesBookingSubmissionRequest'];
export type SalesBookingSubmissionResponse =
  components['schemas']['SalesBookingSubmissionResponse'];

export type UseMutateSubmitSalesBookingFormVariables = {
  formName: SalesBookingFormName;
  body: SalesBookingSubmissionRequest;
};

// HubSpot submissions can take a few seconds end-to-end (server proxies the
// call). 30s mirrors useMutateBookMeeting and is generous enough to absorb
// transient backend slowness without leaving the user staring at a spinner.
const REQUEST_TIMEOUT_MS = 30_000;

function applyOptimisticSalesBookingSubmission(
  user: UserResponse,
  formName: components['schemas']['SalesBookingFormName']
): UserResponse {
  return produce(user, (draft) => {
    if (!draft.sales_booking_forms_submitted) {
      draft.sales_booking_forms_submitted = {};
    }
    const existing = draft.sales_booking_forms_submitted[formName];
    const now = new Date().toISOString();
    draft.sales_booking_forms_submitted[formName] = {
      last_submitted_at: now,
      submit_count: (existing?.submit_count ?? 0) + 1,
    };
  });
}

/**
 * POST /api/v2/ml-scribe/sales-booking/{form_name} — submits a sales-booking
 * form whose HubSpot portal/form id and post-submit handler are held
 * server-side. Callers supply only the form fields and optional tracking
 * context (hutk, page_uri, page_name).
 *
 * Optimistically updates {@link UserResponse.sales_booking_forms_submitted}
 * and invalidates the user query on settle.
 *
 * Use `error.code === 'ECONNABORTED'` (axios timeout) to distinguish timeouts
 * from other failures when rendering toasts.
 */
export function useMutateSubmitSalesBookingForm() {
  const queryClient = useQueryClient();

  return useMutation<
    SalesBookingSubmissionResponse,
    AxiosError,
    UseMutateSubmitSalesBookingFormVariables,
    { previousUser?: UserResponse }
  >({
    mutationFn: async ({ formName, body }) => {
      const { data } = await scribeHttpV2.post<SalesBookingSubmissionResponse>(
        `/sales-booking/${encodeURIComponent(formName)}`,
        body,
        { timeout: REQUEST_TIMEOUT_MS }
      );
      return data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser) {
        queryClient.setQueryData<UserResponse>(
          [GET_USER],
          applyOptimisticSalesBookingSubmission(previousUser, variables.formName)
        );
      }

      return { previousUser };
    },
    onError: (error, variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error(`Failed to submit sales booking form '${variables.formName}'`, error);
    },
    onSettled: async () => {
      await invalidateCurrentUser();
    },
  });
}
